<template>
  <div class="tra" v-if="store.querent_id">
    <!-- Their portrait (2026-10-23, Chris: in place of the hamburger) — tucks
         the account chip, Messages, the balance and sign out. -->
    <div class="tra-menu" ref="menuRef">
      <button class="tra-burger" :class="{ open: menuOpen }" @click="menuOpen = !menuOpen"
              :title="menuTitle" :aria-label="menuTitle" :aria-expanded="menuOpen">
        <span class="tra-me">
          <img v-if="store.querent_avatar_url" :src="store.querent_avatar_url" alt="" />
          <span v-else class="tra-avatar-ph">{{ initial }}</span>
        </span>
        <!-- A tiny dot: there are messages inside (2026-10-23, Chris). -->
        <span v-if="store.message_count > 0" class="tra-dot" data-testid="menu-message-dot" aria-hidden="true"></span>
      </button>

      <transition name="tra-dd">
        <div v-if="menuOpen" class="tra-dropdown">
          <!-- Account — avatar + name → opens the Account modal. -->
          <button class="tra-acct" @click="openAccount">
            <span class="tra-avatar">
              <img v-if="store.querent_avatar_url" :src="store.querent_avatar_url" :alt="store.querent_name" />
              <span v-else class="tra-avatar-ph">{{ initial }}</span>
            </span>
            <span class="tra-name">{{ store.querent_name || 'Account' }}</span>
          </button>

          <!-- Messages (moved inside the hamburger, 2026-10-23). -->
          <button class="tra-row" data-testid="menu-messages" @click="openMessages">
            <i class="fas fa-comment-dots"></i>
            <span>Messages</span>
            <span v-if="store.message_count > 0" class="tra-badge">{{ store.message_count > 99 ? '99+' : store.message_count }}</span>
          </button>

          <!-- The carat balance (moved here from the top-left, 2026-10-23) → the shop. -->
          <button v-if="showBalance" class="tra-balance" data-testid="menu-balance" @click="openStore">
            <CaratMark :size="16" title="Carat balance" />
            <span class="tra-balance-n">{{ balanceKnown ? store.querent_carat_balance : '' }}</span>
            <span class="tra-balance-l">carats</span>
          </button>

          <button class="tra-signout" @click="signOut">
            <i class="fas fa-sign-out-alt"></i> Sign out
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
/**
 * TopRightAccount — their portrait as the menu (a tiny dot on it when there are messages)
 * that tucks the account chip (opens the Account modal), Messages, the balance
 * and Sign out. The Mirror
 * avatar isn't on the store by default, so we hydrate it here on first mount.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { store } from '@/store'
import { getMirrorProfile } from '@/api-service'
import CaratMark from '@/components/ui/CaratMark.vue'

defineProps({
  // The balance row: off during a new person's introduction (storeHidden).
  showBalance: { type: Boolean, default: false },
  // No figure until a server has given us one (store.querent_carat_balance_loaded).
  balanceKnown: { type: Boolean, default: false },
})
const emit = defineEmits(['sign-out', 'open-store'])

const menuOpen = ref(false)
const menuRef = ref(null)
const menuTitle = computed(() => (store.message_count > 0 ? 'Your account · new messages' : 'Your account'))
const initial = computed(() => (store.querent_name || '✦').trim().charAt(0).toUpperCase())

function openAccount() { store.account_modal_open = true; menuOpen.value = false }
function signOut() { menuOpen.value = false; emit('sign-out') }
function openMessages() { menuOpen.value = false; store.messages_open = true }
function openStore() { menuOpen.value = false; emit('open-store') }
function onDocClick(e) {
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(e.target)) menuOpen.value = false
}

onMounted(async () => {
  document.addEventListener('click', onDocClick)
  if (!store.querent_id || store.querent_avatar_url || !store.session_id) return
  try {
    const d = await getMirrorProfile(store.session_id)
    if (d) {
      store.querent_avatar_url = d.avatar_url || ''
      if (!store.querent_name && d.nick_name) store.querent_name = d.nick_name
    }
  } catch (e) { /* avatar is best-effort */ }
})
onUnmounted(() => document.removeEventListener('click', onDocClick))
</script>

<style scoped>
/* Fixed top-right corner: their portrait. Off the window's edge (2026-10-25,
   Chris), clear of a phone's notch and rounded corners. The size and place are
   the app's --tra-* tokens (App.vue), which content sharing the corner clears. */
.tra { position: fixed; top: var(--tra-top, max(10px, env(safe-area-inset-top))); right: var(--tra-right, max(14px, env(safe-area-inset-right)));
  z-index: 1000; display: flex; align-items: center; gap: 6px; }
.tra-dot { position: absolute; top: 0; right: 0; width: 10px; height: 10px; border-radius: 50%;
  background: #C9A94E; box-shadow: 0 0 0 2px #170a2b; }
.tra-row { display: flex; align-items: center; gap: 10px; cursor: pointer; font-family: inherit;
  background: none; border: none; border-radius: 9px; padding: 9px 12px; width: 100%; text-align: left;
  font-size: 14px; color: #efe4c6; transition: background 0.15s; }
.tra-row:hover { background: rgba(102,51,153,0.32); }
.tra-row i { color: #c9b8ec; width: 16px; text-align: center; }
.tra-badge { margin-left: auto; min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px; background: #C9A94E; color: #1a0f2e; font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; }

.tra-menu { position: relative; }
.tra-burger { position: relative; width: var(--tra-size, 42px); height: var(--tra-size, 42px); border-radius: 50%; padding: 0; cursor: pointer;
  border: 1.5px solid rgba(201,169,78,0.55); background: #1a0c2e; font-family: inherit;
  display: flex; align-items: center; justify-content: center; transition: border-color 0.15s, box-shadow 0.15s; }
.tra-burger:hover, .tra-burger.open { border-color: #f0d9a3; box-shadow: 0 0 0 3px rgba(201,169,78,0.18); }
.tra-me { width: 100%; height: 100%; border-radius: 50%; overflow: hidden; display: block; }
.tra-me img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Dropdown — anchored under the portrait, right-aligned. */
.tra-dropdown { position: absolute; top: calc(100% + 6px); right: 0; min-width: 196px;
  background: #170a2b; border: 1px solid rgba(155,134,201,0.3); border-radius: 12px; padding: 6px;
  box-shadow: 0 16px 40px -12px rgba(0,0,0,0.7); display: flex; flex-direction: column; gap: 4px; z-index: 1001; }

.tra-acct { display: flex; align-items: center; gap: 10px; cursor: pointer; font-family: inherit;
  background: none; border: none; border-radius: 9px; padding: 8px 10px; width: 100%; text-align: left;
  transition: background 0.15s; }
.tra-acct:hover { background: rgba(102,51,153,0.32); }
.tra-avatar { width: 30px; height: 30px; border-radius: 50%; overflow: hidden; flex: none;
  background: #1a0c2e; border: 1px solid rgba(240,217,163,0.3); }
.tra-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.tra-avatar-ph { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;
  font-family: 'Cinzel', serif; font-size: 16px; color: #C9A94E; }
.tra-name { font-size: 14px; color: #efe4c6; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.tra-balance { display: flex; align-items: center; gap: 8px; cursor: pointer; font-family: inherit;
  background: none; border: none; border-radius: 9px; padding: 9px 12px; width: 100%; text-align: left;
  font-size: 14px; color: #efe4c6; transition: background 0.15s; }
.tra-balance:hover { background: rgba(102,51,153,0.32); }
.tra-balance-n { font-weight: 600; color: #f0d9a3; }
.tra-balance-l { font-size: 13px; color: rgba(232,216,255,0.7); }

.tra-signout { display: flex; align-items: center; gap: 8px; cursor: pointer; font-family: inherit;
  background: none; border: none; border-radius: 9px; padding: 9px 12px; width: 100%; text-align: left;
  font-size: 13px; color: rgba(232,216,255,0.85); border-top: 1px solid rgba(155,134,201,0.18);
  transition: background 0.15s, color 0.15s; }
.tra-signout:hover { background: rgba(160,60,60,0.22); color: #ff9c9c; }

.tra-dd-enter-active, .tra-dd-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.tra-dd-enter-from, .tra-dd-leave-to { opacity: 0; transform: translateY(-6px); }
</style>
